import axios from 'axios';
import { ApiRequestError, type ApiErrorBody } from '@app-types/api';
import { getAccessToken } from '@utils/authStorage';
import {
  isAuthErrorReason,
  notifyAuthErrorOnce,
} from '@utils/authEvents';
import { WIDGET_ERROR_MESSAGES } from '@utils/widgetErrorMessages';

const parseTimeout = (value: string | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 15000;
};

const normalizeApiPath = (value: string | undefined, fallback: string): string => {
  const path = value?.trim() || fallback;
  return path.startsWith('/') ? path : `/${path}`;
};

const joinBaseUrl = (baseUrl: string | undefined, path: string): string => {
  const normalizedPath = normalizeApiPath(path, '/api/auth');
  const normalizedBaseUrl = baseUrl?.trim().replace(/\/+$/, '') || '';
  return normalizedBaseUrl ? `${normalizedBaseUrl}${normalizedPath}` : normalizedPath;
};

export const authClient = axios.create({
  baseURL: joinBaseUrl(
    process.env.REACT_APP_ESMART_API_BASE_URL,
    process.env.REACT_APP_AUTH_API_PATH || '/api/auth',
  ),
  timeout: parseTimeout(process.env.REACT_APP_API_TIMEOUT_MS),
  headers: {
    Accept: 'application/json',
  },
});

const buildErrorText = (message?: unknown, reason?: unknown): string =>
  [reason, message]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

const isTokenExpiredMessage = (text: string): boolean =>
  text.includes('token expired') || text.includes('token was expired');

/** Detects backend auth failures: 401 with a known token reason/message. */
const isAuthSessionError = (caught: unknown): boolean => {
  if (!axios.isAxiosError(caught) || caught.response?.status !== 401) return false;
  const data = caught.response?.data;
  if (typeof data !== 'object' || data === null) return false;

  const { message, reason } = data as {
    message?: unknown;
    reason?: unknown;
  };

  return isAuthErrorReason(reason) || isTokenExpiredMessage(buildErrorText(message, reason));
};

/** Shows one shared auth notification while keeping the current widget mounted. */
const handleAuthSessionError = (): void => {
  if (typeof window === 'undefined') return;
  notifyAuthErrorOnce();
};

/**
 * Builds a data API client for one backend service prefix. Kept separate from
 * `authClient` so only data requests carry the `x-access-token` header.
 * Auth failures surface a single toast; iframe param validation handles hard
 * blocking. The token is never logged nor forwarded via `postMessage`.
 */
const createDataClient = (apiPath: string) => {
  const client = axios.create({
    baseURL: joinBaseUrl(process.env.REACT_APP_ESMART_API_BASE_URL, apiPath),
    timeout: parseTimeout(process.env.REACT_APP_API_TIMEOUT_MS),
    headers: {
      Accept: 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.set('x-access-token', token);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isAuthSessionError(error)) {
        handleAuthSessionError();
      }
      return Promise.reject(error);
    },
  );

  return client;
};

/** Summary service (`summary-api`) endpoints. */
export const summaryClient = createDataClient('/api/summary');

/** Fundamentals service (`fundamentals-api`) endpoints. */
export const fundamentalsClient = createDataClient('/api/fundamentals');

/** Comparison service endpoints. */
export const comparisonClient = createDataClient('/api/comparison');

/** Dividend service (`dividends-api`) endpoints. */
export const dividendsClient = createDataClient('/api/dividends');

/** Investment indicators service endpoints. */
export const indicatorsClient = createDataClient('/api/indicators');

/** Timely Disclosure / Instructions service endpoints. */
export const disclosuresClient = createDataClient('/api/disclosures');

const normalizeErrorBody = (data: unknown): ApiErrorBody | undefined => {
  if (!data) return undefined;
  if (typeof data === 'string') return { message: data };
  if (typeof data === 'object' && data !== null) {
    const { message, reason } = data as {
      message?: unknown;
      reason?: unknown;
    };
    const body: ApiErrorBody = {};
    if (typeof message === 'string') body.message = message;
    if (typeof reason === 'string') body.reason = reason;
    return body.message || body.reason ? body : undefined;
  }
  return undefined;
};

export const isAuthRequestError = (caught: unknown): boolean => (
  caught instanceof ApiRequestError &&
  caught.status === 401 &&
  (
    isAuthErrorReason(caught.body?.reason) ||
    isTokenExpiredMessage(buildErrorText(caught.body?.message, caught.body?.reason))
  )
);

/**
 * True only for a converted `ApiRequestError` that never received a response:
 * an Axios timeout (`isTimeout`) or a network/no-response failure such as
 * offline, DNS, CORS, or connection error (`isNetworkError`). HTTP 4xx/5xx
 * responses are response-backed and are not classified here.
 */
export const isNoResponseRequestError = (caught: unknown): boolean =>
  caught instanceof ApiRequestError &&
  (caught.isTimeout || caught.isNetworkError);

/**
 * Page/section guard: whether a caught request error should raise a toast.
 * Recognized auth errors (handled once by the shared client) and no-response
 * errors (timeout/network, silent per product decision) are suppressed;
 * response-backed ticker/mkt/tab and generic failures remain notifiable.
 */
export const shouldNotifyDataRequestError = (caught: unknown): boolean =>
  !isAuthRequestError(caught) && !isNoResponseRequestError(caught);

export const getApiParamErrorNotificationMessage = (
  caught: unknown,
): string | undefined => {
  if (!(caught instanceof ApiRequestError)) return undefined;

  const text = buildErrorText(caught.body?.message ?? caught.message, caught.body?.reason);

  if (
    text.includes('incorrect ticker exchange') ||
    text.includes('incorrect exchange') ||
    text.includes('invalid_mkt') ||
    text.includes('invalid_market') ||
    text.includes('ticker_exchange')
  ) {
    return WIDGET_ERROR_MESSAGES.mkt;
  }

  if (
    text.includes('incorrect ticker') ||
    text.includes('unrecognized ticker') ||
    text.includes('unrecoglized ticker') ||
    text.includes('invalid ric') ||
    text.includes('invalid_ticker') ||
    text.includes('ticker_not_found')
  ) {
    return WIDGET_ERROR_MESSAGES.ticker;
  }

  if (
    text.includes('unexpected tab') ||
    text.includes('invalid_tab') ||
    text.includes('tab_not_found')
  ) {
    return WIDGET_ERROR_MESSAGES.tab;
  }

  return undefined;
};

export const toApiRequestError = (caught: unknown): ApiRequestError => {
  if (caught instanceof ApiRequestError) return caught;

  if (axios.isAxiosError(caught)) {
    const body = normalizeErrorBody(caught.response?.data);
    const status = caught.response?.status;
    return new ApiRequestError(body?.message || caught.message || 'API request failed.', {
      status,
      body,
      isNetworkError: !caught.response,
      isTimeout: caught.code === 'ECONNABORTED',
    });
  }

  return new ApiRequestError(
    caught instanceof Error ? caught.message : 'API request failed.',
  );
};
